/**
 * BidConfidencePanel — single source of truth for "should we bid, and
 * will we win" inside a channel.
 *
 * Replaces the old BidMatrixPanel (PQC/Tech/Fin traffic-light) and the
 * standalone Win Probability surface. Folds both into one view:
 *   • headline: probability % + verdict (Go / Conditional / No-Go) +
 *     readiness % (from PQC/Tech/Fin matrix).
 *   • "Why" — top positive + top negative drivers (with contribution
 *     in percentage points so users see exactly how each signal moved
 *     the number).
 *   • Readiness drill-down — collapsible PQC / Technical / Financial
 *     sections, traffic-light per criterion (same data the old panel
 *     showed; kept because users want to act on individual gaps).
 *   • Similar past bids — outcome + similarity %, helps justify the
 *     forecast.
 *
 * Talks to GET /api/v2/rfp/{id}/bid-confidence which composes
 * bid_matrix + win_probability server-side.
 */
import React, { useEffect, useState, useCallback } from "react";
import { v2 } from "@/lib/v2Api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Target, CheckCircle2, XCircle, AlertCircle, Loader2,
  RefreshCcw, ShieldCheck, Wrench, IndianRupee, TrendingUp, TrendingDown,
  ChevronDown, ChevronRight, Sparkles, Brain, Trophy, AlertTriangle, Zap,
} from "lucide-react";

const PRETTY_FEATURE = {
  eligibility: "Eligibility match",
  technical_fit: "Technical fit",
  client_history: "Client history",
  product_coverage: "Product coverage",
  price_competitiveness: "Price competitiveness",
  timeline: "Timeline",
  competition: "Competition",
  compliance: "Compliance",
};

function VerdictBadge({ verdict }) {
  if (verdict === "go") {
    return (
      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-sm px-3 py-1">
        <CheckCircle2 className="w-4 h-4 mr-1.5" /> Go
      </Badge>
    );
  }
  if (verdict === "no_go") {
    return (
      <Badge className="bg-red-100 text-red-700 border-red-200 text-sm px-3 py-1">
        <XCircle className="w-4 h-4 mr-1.5" /> No-Go
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-sm px-3 py-1">
      <AlertCircle className="w-4 h-4 mr-1.5" /> Conditional
    </Badge>
  );
}

function statusBadge(s) {
  if (s === "met") {
    return (
      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
        <CheckCircle2 className="w-3 h-3 mr-1" /> Met
      </Badge>
    );
  }
  if (s === "not_met") {
    return (
      <Badge className="bg-red-100 text-red-700 border-red-200">
        <XCircle className="w-3 h-3 mr-1" /> Missing
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-700 border-amber-200">
      <AlertCircle className="w-3 h-3 mr-1" /> Review
    </Badge>
  );
}

function pctColor(pct) {
  if (pct >= 70) return "text-emerald-600";
  if (pct >= 50) return "text-amber-600";
  if (pct >= 30) return "text-orange-600";
  return "text-red-600";
}

function pctBg(pct) {
  if (pct >= 70) return "bg-emerald-500";
  if (pct >= 50) return "bg-amber-500";
  if (pct >= 30) return "bg-orange-500";
  return "bg-red-500";
}

function DriverRow({ d }) {
  const positive = d.direction === "positive";
  const Icon = positive ? TrendingUp : (d.direction === "negative" ? TrendingDown : AlertCircle);
  const tone = positive
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : d.direction === "negative"
    ? "text-red-700 bg-red-50 border-red-200"
    : "text-slate-700 bg-slate-50 border-slate-200";
  const sign = positive ? "+" : (d.direction === "negative" ? "−" : "");
  const display = Math.abs(d.contribution_pts).toFixed(1);
  return (
    <div className={`flex items-start gap-3 border rounded-lg px-3 py-2 ${tone}`}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium">
            {PRETTY_FEATURE[d.name] || d.name}
          </span>
          <span className="text-sm font-semibold tabular-nums">
            {sign}{display} pts
          </span>
        </div>
        <p className="text-xs opacity-80 mt-0.5">{d.why}</p>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, items, accent, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const met = items.filter((x) => x.status === "met").length;
  const notMet = items.filter((x) => x.status === "not_met").length;
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader
        className="pb-2 cursor-pointer hover:bg-slate-50"
        onClick={() => setOpen((o) => !o)}
      >
        <CardTitle className="flex items-center gap-2 text-base">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Icon className={`w-5 h-5 ${accent}`} />
          {title}
          <Badge variant="outline" className="ml-1">
            {met} of {items.length} met
          </Badge>
          {notMet > 0 && (
            <Badge className="bg-red-100 text-red-700 border-red-200">
              {notMet} missing
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent className="space-y-2">
          {items.length === 0 ? (
            <p className="text-sm text-slate-500 italic">
              No {title.toLowerCase()} criteria extracted from this tender.
            </p>
          ) : (
            items.map((it, i) => (
              <div
                key={`${title}-${i}`}
                className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800">{it.criterion}</p>
                    {it.evidence && it.status === "met" && (
                      <p className="text-xs text-emerald-700 mt-1">
                        <CheckCircle2 className="inline w-3 h-3 mr-1" />
                        {it.evidence}
                      </p>
                    )}
                    {it.missing && it.status !== "met" && (
                      <p className="text-xs text-red-700 mt-1">
                        <AlertCircle className="inline w-3 h-3 mr-1" />
                        {it.missing}
                      </p>
                    )}
                    {it.page && (
                      <p className="text-xs text-slate-400 mt-1">page {it.page}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0">{statusBadge(it.status)}</div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      )}
    </Card>
  );
}

function StrategyBriefCard({ rfpId }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false) => {
    if (!rfpId) return;
    force ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const r = await v2.get(`/rfp/${rfpId}/strategy-brief`, {
        params: force ? { refresh: true } : {},
        timeout: 180000,
      });
      setBrief(r.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "failed");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [rfpId]);
  useEffect(() => { load(false); }, [load]);

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Generating AI strategy brief…
        </CardContent>
      </Card>
    );
  }
  if (error || brief?._warning) {
    return (
      <Card className="border-0 shadow-sm border-amber-200">
        <CardContent className="p-4 text-sm text-amber-700">
          <AlertCircle className="w-4 h-4 inline mr-1.5" />
          {error || brief?._warning}
        </CardContent>
      </Card>
    );
  }
  if (!brief) return null;

  const confTone = brief.confidence === "high"
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : brief.confidence === "low"
    ? "bg-red-100 text-red-700 border-red-200"
    : "bg-amber-100 text-amber-700 border-amber-200";

  return (
    <Card className="border-0 shadow-md bg-gradient-to-br from-violet-50 to-blue-50">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-violet-600" />
            AI Strategy Brief
            {brief.confidence && (
              <Badge className={`ml-1 ${confTone}`}>
                {brief.confidence} confidence
              </Badge>
            )}
          </CardTitle>
          <Button
            variant="ghost" size="sm"
            onClick={() => load(true)} disabled={refreshing}
          >
            <RefreshCcw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {brief.headline && (
          <p className="text-base font-semibold text-slate-800 leading-snug">
            {brief.headline}
          </p>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="bg-white/70 backdrop-blur rounded-lg p-3 border border-emerald-100">
            <div className="flex items-center gap-1.5 mb-2 text-emerald-700 font-medium text-sm">
              <Trophy className="w-4 h-4" /> Why we'd win
            </div>
            <ul className="text-sm text-slate-700 space-y-1.5">
              {(brief.why_we_win || []).map((b, i) => (
                <li key={i} className="flex gap-1.5"><span className="text-emerald-600">•</span><span>{b}</span></li>
              ))}
            </ul>
          </div>
          <div className="bg-white/70 backdrop-blur rounded-lg p-3 border border-red-100">
            <div className="flex items-center gap-1.5 mb-2 text-red-700 font-medium text-sm">
              <AlertTriangle className="w-4 h-4" /> Why we might lose
            </div>
            <ul className="text-sm text-slate-700 space-y-1.5">
              {(brief.why_we_might_lose || []).map((b, i) => (
                <li key={i} className="flex gap-1.5"><span className="text-red-600">•</span><span>{b}</span></li>
              ))}
            </ul>
          </div>
          <div className="bg-white/70 backdrop-blur rounded-lg p-3 border border-blue-100">
            <div className="flex items-center gap-1.5 mb-2 text-blue-700 font-medium text-sm">
              <Zap className="w-4 h-4" /> Do differently
            </div>
            <ul className="text-sm text-slate-700 space-y-1.5">
              {(brief.do_differently || []).map((b, i) => (
                <li key={i} className="flex gap-1.5"><span className="text-blue-600">•</span><span>{b}</span></li>
              ))}
            </ul>
          </div>
        </div>
        {brief.grounded_in && (
          <p className="text-xs text-slate-500 pt-1 border-t border-slate-200/60">
            Grounded in {brief.grounded_in.similar_bid_count} similar past bid(s),
            win probability {Math.round(brief.grounded_in.probability_pct)}%,
            eligibility readiness {Math.round(brief.grounded_in.readiness_pct)}%.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function BidConfidencePanel({ rfpId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (force = false) => {
      if (!rfpId) return;
      force ? setRefreshing(true) : setLoading(true);
      setError(null);
      try {
        const r = await v2.get(`/rfp/${rfpId}/bid-confidence`, {
          params: force ? { refresh: true } : {},
          timeout: 180000,
        });
        setData(r.data || null);
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "failed to load");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [rfpId],
  );

  useEffect(() => {
    load(false);
  }, [load]);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Computing bid confidence…
      </div>
    );
  }
  if (error) {
    return <div className="p-8 text-red-600 text-sm">{error}</div>;
  }
  if (!data) {
    return <div className="p-8 text-slate-500 text-sm">No data.</div>;
  }

  const prob = Math.round(Number(data.probability_pct || 0));
  const readiness = Math.round(Number(data.readiness_pct || 0));
  const positives = data.top_positive || [];
  const negatives = data.top_negative || [];
  const risks = data.top_risks || [];
  const similar = data.similar_bids || [];
  const readinessSections = data.readiness || {};

  return (
    <div className="p-4 max-w-6xl mx-auto space-y-4">
      {/* ---------- Headline ---------- */}
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              Bid Confidence
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(true)}
              disabled={refreshing}
            >
              <RefreshCcw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Re-scoring…" : "Refresh"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Probability */}
            <div className="rounded-lg border border-slate-200 p-4 text-center">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                Win probability
              </div>
              <div className={`text-5xl font-bold tabular-nums ${pctColor(prob)}`}>
                {prob}<span className="text-2xl">%</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full ${pctBg(prob)}`}
                  style={{ width: `${prob}%` }}
                />
              </div>
            </div>
            {/* Verdict */}
            <div className="rounded-lg border border-slate-200 p-4 text-center flex flex-col items-center justify-center">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                Verdict
              </div>
              <VerdictBadge verdict={data.verdict} />
              <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                {data.verdict === "go"
                  ? "Strong fit — pursue aggressively."
                  : data.verdict === "no_go"
                  ? "Major eligibility gaps — likely skip."
                  : "Viable with caveats — close gaps before pricing."}
              </p>
            </div>
            {/* Readiness */}
            <div className="rounded-lg border border-slate-200 p-4 text-center">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                Eligibility readiness
              </div>
              <div className={`text-5xl font-bold tabular-nums ${pctColor(readiness)}`}>
                {readiness}<span className="text-2xl">%</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full ${pctBg(readiness)}`}
                  style={{ width: `${readiness}%` }}
                />
              </div>
              <div className="text-xs text-slate-500 mt-2">
                PQC + Technical + Financial
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ---------- AI Strategy Brief ---------- */}
      <StrategyBriefCard rfpId={rfpId} />

      {/* ---------- Why ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              What's helping
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {positives.length === 0 ? (
              <p className="text-sm text-slate-500 italic">
                No standout positive drivers — score is balanced.
              </p>
            ) : (
              positives.map((d, i) => <DriverRow key={`pos-${i}`} d={d} />)
            )}
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              What's hurting
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {negatives.length === 0 ? (
              <p className="text-sm text-slate-500 italic">
                No major negative drivers.
              </p>
            ) : (
              negatives.map((d, i) => <DriverRow key={`neg-${i}`} d={d} />)
            )}
            {risks.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  Additional risks
                </div>
                <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                  {risks.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ---------- Similar bids ---------- */}
      {similar.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Similar historical bids</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {similar.map((s, i) => (
              <div
                key={s.bid_id || i}
                className="flex items-center justify-between border border-slate-200 rounded-lg p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {s.bid_name}
                  </div>
                  <div className="text-xs text-slate-500">
                    {s.client_name || "—"} · {s.client_type || "—"}
                    {s.our_rank ? ` · ranked L${s.our_rank}` : ""}
                  </div>
                  {s.lessons_learned && (
                    <p className="text-xs text-slate-600 mt-1 italic">
                      "{s.lessons_learned}"
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <Badge
                    className={
                      s.outcome === "won"
                        ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                        : s.outcome === "lost"
                        ? "bg-red-100 text-red-700 border-red-200"
                        : "bg-slate-100 text-slate-700 border-slate-200"
                    }
                  >
                    {s.outcome}
                  </Badge>
                  <span className="text-xs text-slate-500 tabular-nums">
                    {Math.round(Number(s.similarity || 0) * 100)}% similar
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ---------- Readiness drill-down ---------- */}
      <div className="space-y-3">
        <div className="text-sm font-medium text-slate-700 px-1">
          Eligibility readiness — line by line
        </div>
        <Section
          title="PQC"
          icon={ShieldCheck}
          accent="text-blue-600"
          items={readinessSections.pqc || []}
          defaultOpen
        />
        <Section
          title="Technical"
          icon={Wrench}
          accent="text-purple-600"
          items={readinessSections.technical || []}
        />
        <Section
          title="Financial"
          icon={IndianRupee}
          accent="text-emerald-600"
          items={readinessSections.financial || []}
        />
      </div>
    </div>
  );
}

export default BidConfidencePanel;
